// @flow

import { mergeMap, take } from 'rxjs/operators';
import { empty, of } from 'rxjs';
import { combineEpics, ofType } from 'redux-observable';

import type { ActionsObservable, StateObservable, Middleware } from '../common/typings';
import bugsnag from '../bugsnag';
import config from '../config';
import SlingStorage from '../common/SlingStorage';
import {
  LOG_IN,
  LOG_IN_EXTERNAL_USER,
  LOG_IN_FAILURE,
  signOut,
  integrationLogIn
} from '../authentication/actions';
import { REQUEST_FAILURE, addErrorToast } from '../utils/actions';
import { STORAGE_GUID_FIELD, STORAGE_TIME_FORMAT_FIELD, STORAGE_SESSION_FIELD } from '../constants';
import { getPathFromUrl } from '../utils/Utils';

import { INITIALIZE, INITIALIZE_SUCCESS, SCHEDULE_REDIRECTION, initializeSuccess } from './actions';

const { bugsnagClient } = bugsnag;

export function initializeEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(INITIALIZE),
    mergeMap(() => {
      const urlParams = new URLSearchParams(window.location.search);

      let usTimeFormat = urlParams.get(STORAGE_TIME_FORMAT_FIELD);
      if (usTimeFormat) {
        SlingStorage.setItem(STORAGE_TIME_FORMAT_FIELD, usTimeFormat);
      } else {
        usTimeFormat = SlingStorage.getItem(STORAGE_TIME_FORMAT_FIELD);
      }

      if (typeof usTimeFormat === 'string') {
        usTimeFormat = usTimeFormat === 'true';
      }

      let kioskSessionId = urlParams.get(STORAGE_SESSION_FIELD);
      if (kioskSessionId) {
        SlingStorage.setItem(STORAGE_SESSION_FIELD, kioskSessionId);
      } else {
        kioskSessionId = SlingStorage.getItem(STORAGE_SESSION_FIELD);
      }

      if (typeof kioskSessionId === 'string') {
        kioskSessionId = Number(kioskSessionId);
      }

      const origin = urlParams.get('origin');
      const orgId = urlParams.get('orgId');
      const staffId = urlParams.get('staffId');
      const nonce = urlParams.get('nonce');

      if (origin && orgId && staffId && nonce) {
        return of(
          integrationLogIn(orgId, staffId, origin, nonce, Boolean(usTimeFormat), kioskSessionId)
        );
      }

      let guid = urlParams.get(STORAGE_GUID_FIELD);
      if (guid) {
        SlingStorage.setItem(STORAGE_GUID_FIELD, guid);
      } else {
        guid = SlingStorage.getItem(STORAGE_GUID_FIELD);
        if (!guid) {
          const current_query_params = window.location.search;
          window.location.replace(`${config.KIOSK_APP_URL}/setup${current_query_params}`);
          return empty();
        }
      }

      if (window.history.replaceState) {
        window.history.replaceState({}, 'Sling Kiosk', getPathFromUrl(window.location.href));
      }

      return of(
        initializeSuccess(((guid: any): string), Boolean(usTimeFormat), null, kioskSessionId)
      );
    })
  );
}

export function updateApiStateEpic(
  action$: ActionsObservable,
  state$: StateObservable,
  { Api }: Middleware
) {
  return action$.pipe(
    ofType(INITIALIZE_SUCCESS, LOG_IN, LOG_IN_EXTERNAL_USER, LOG_IN_FAILURE),
    // INITIALIZE_SUCCESS - because we need guid that's set in store with this action
    // LOG_IN - because we have to preset PIN for token creation
    // LOG_IN_FAILURE - because then token changes back to null
    mergeMap(() => {
      Api.state = state$.value;
      return empty();
    })
  );
}

export function requestFailureEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(REQUEST_FAILURE),
    mergeMap(action => {
      const { error } = action.payload;
      bugsnagClient.notify(error);
      return of(addErrorToast(error), signOut());
    })
  );
}

export function scheduleRedirectionEpic(action$: ActionsObservable) {
  return action$.pipe(
    ofType(SCHEDULE_REDIRECTION),
    take(1),
    mergeMap(action => {
      // TODO how to clear timeouts like this?
      setTimeout(() => {
        const current_query_params = window.location.search;
        window.location.replace(`${config.KIOSK_APP_URL}/setup${current_query_params}`);
      }, action.payload.milisecondsToRedirect);
      return empty();
    })
  );
}

export default combineEpics(
  initializeEpic,
  updateApiStateEpic,
  requestFailureEpic,
  scheduleRedirectionEpic
);
